import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import { Bell, CheckCircle, XCircle, Calendar, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

interface PendingDossier {
  id: string;
  client_name: string;
  employer_first_name: string;
  employer_last_name: string;
  employer_company: string;
  start_date: string;
  description: string | null;
}

export const ProfessionalPendingDossiersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('professional');
  const [acceptModalDossier, setAcceptModalDossier] = useState<PendingDossier | null>(null);
  const [rejectModalDossier, setRejectModalDossier] = useState<PendingDossier | null>(null);
  const [acceptToelichting, setAcceptToelichting] = useState('');
  const [rejectToelichting, setRejectToelichting] = useState('');

  const { data: dossiers, isLoading } = useQuery({
    queryKey: ['professional-pending-dossiers'],
    queryFn: async () => {
      const response = await api.get('/professional/dossiers/pending-acceptance');
      return response.data.data || [];
    }
  });

  const acceptMutation = useMutation({
    mutationFn: async ({ id, toelichting }: { id: string; toelichting?: string }) => {
      const response = await api.post(`/professional/dossiers/${id}/accept`, {
        toelichting: toelichting || undefined
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('pendingDossiers.acceptedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['professional-pending-dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['professional-dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setAcceptModalDossier(null);
      setAcceptToelichting('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('pendingDossiers.acceptError'));
    }
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, toelichting }: { id: string; toelichting: string }) => {
      const response = await api.post(`/professional/dossiers/${id}/reject`, {
        toelichting
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('pendingDossiers.rejectedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['professional-pending-dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['employer-dossiers'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setRejectModalDossier(null);
      setRejectToelichting('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('pendingDossiers.rejectError'));
    }
  });

  const handleAccept = () => {
    if (!acceptModalDossier) return;
    acceptMutation.mutate({
      id: acceptModalDossier.id,
      toelichting: acceptToelichting.trim() || undefined
    });
  };

  const handleReject = () => {
    if (!rejectModalDossier || !rejectToelichting.trim()) {
      toast.error(t('pendingDossiers.toelichtingRequired'));
      return;
    }
    rejectMutation.mutate({
      id: rejectModalDossier.id,
      toelichting: rejectToelichting.trim()
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('pendingDossiers.loading')}</p>
        </div>
      </div>
    );
  }

  const pendingList = (dossiers || []) as PendingDossier[];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bell className="w-7 h-7 text-amber-500" />
          {t('pendingDossiers.title')}
        </h1>
        <p className="mt-1 text-gray-600">
          {t('pendingDossiers.subtitle')}
        </p>
      </div>

      {pendingList.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Bell className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('pendingDossiers.noNotificationsTitle')}
            </h2>
            <p className="text-gray-600">
              {t('pendingDossiers.noNotificationsDesc')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {pendingList.map((dossier) => (
            <Card key={dossier.id} className="border-amber-200 bg-amber-50/30">
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{dossier.client_name}</h3>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        {t('pendingDossiers.waitingAcceptance')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {dossier.employer_company || `${dossier.employer_first_name} ${dossier.employer_last_name}`}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(dossier.start_date).toLocaleDateString('nl-NL')}
                      </span>
                    </div>
                    {dossier.description && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">{dossier.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      onClick={() => setRejectModalDossier(dossier)}
                      className="border-red-200 text-red-700 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      {t('pendingDossiers.reject')}
                    </Button>
                    <Button
                      onClick={() => setAcceptModalDossier(dossier)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {t('pendingDossiers.accept')}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Accept modal */}
      {acceptModalDossier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pendingDossiers.acceptModalTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('pendingDossiers.acceptModalDesc', { name: acceptModalDossier.client_name })}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pendingDossiers.toelichtingOptional')}
                </label>
                <textarea
                  rows={3}
                  value={acceptToelichting}
                  onChange={(e) => setAcceptToelichting(e.target.value)}
                  placeholder={t('pendingDossiers.toelichtingPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAcceptModalDossier(null);
                    setAcceptToelichting('');
                  }}
                >
                  {t('pendingDossiers.cancel')}
                </Button>
                <Button
                  onClick={handleAccept}
                  disabled={acceptMutation.isPending}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {acceptMutation.isPending ? t('pendingDossiers.busy') : t('pendingDossiers.accept')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Reject modal */}
      {rejectModalDossier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('pendingDossiers.rejectModalTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('pendingDossiers.rejectModalDesc', { name: rejectModalDossier.client_name })}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('pendingDossiers.toelichtingRequired')} <span className="text-red-600">*</span>
                </label>
                <textarea
                  rows={3}
                  value={rejectToelichting}
                  onChange={(e) => setRejectToelichting(e.target.value)}
                  placeholder={t('pendingDossiers.rejectToelichtingPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectModalDossier(null);
                    setRejectToelichting('');
                  }}
                >
                  {t('pendingDossiers.cancel')}
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={rejectMutation.isPending || !rejectToelichting.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {rejectMutation.isPending ? t('pendingDossiers.busy') : t('pendingDossiers.reject')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
